/**
 * TCL Before / After — draggable image-comparison slider.
 *
 * The reveal position lives in ONE place: the `--tcl-ba-pos` custom property on
 * `.tl-cba__media`. It drives the clip of the "before" layer, the divider handle,
 * and the accessible range value at once, so they can never drift.
 *
 * Interaction is powered by GSAP Draggable (the theme's animation library; see
 * docs/adr/0006), with InertiaPlugin adding optional flick momentum. Draggable
 * transforms an invisible PROXY element rather than the visible handle — the
 * handle stays positioned by CSS `left: var(--tcl-ba-pos)`, so there is a single
 * source of truth and no transform/left double-move.
 *
 * Pure progressive enhancement:
 *   - no JS / GSAP or Draggable missing → static split at the Start position;
 *     the native <input type="range"> still adjusts it (keyboard + pointer).
 *   - prefers-reduced-motion: reduce      → drag works, inertia/flick is off.
 * The media has a fixed aspect-ratio, so there is zero layout shift in all cases.
 */

(function () {
  'use strict';

  var REDUCE_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
  }

  function initSlider(root) {
    if (root.dataset.tclBaBound === 'true') return;
    root.dataset.tclBaBound = 'true';

    var media = root.querySelector('[data-tcl-ba-media]');
    var proxy = root.querySelector('[data-tcl-ba-proxy]');
    var range = root.querySelector('[data-tcl-ba-range]');
    if (!media || !range) return;

    // Single source of truth: write the position everywhere that reads it.
    function setPosition(pct) {
      pct = clamp(pct, 0, 100);
      media.style.setProperty('--tcl-ba-pos', pct + '%');
      range.value = pct;
      range.setAttribute('aria-valuenow', Math.round(pct));
      range.setAttribute('aria-valuetext', Math.round(pct) + '% ' + (root.dataset.afterLabel || 'after'));
      return pct;
    }

    var gsap = window.gsap;
    var Draggable = window.Draggable;
    var Inertia = window.InertiaPlugin;

    // Fallback path: no GSAP / no Draggable. The native range carries the whole
    // interaction (pointer + keyboard); nothing else to wire up.
    if (!gsap || !Draggable) {
      range.addEventListener('input', function () {
        setPosition(parseFloat(range.value));
      });
      setPosition(parseFloat(range.value));
      return;
    }

    gsap.registerPlugin(Draggable);
    var flick =
      root.dataset.flick !== 'false' && !REDUCE_MOTION && !!Inertia;
    if (flick) gsap.registerPlugin(Inertia);

    var width = media.clientWidth || 1;

    function pctToX(pct) {
      return (clamp(pct, 0, 100) / 100) * width;
    }

    var dragInstance = Draggable.create(proxy, {
      type: 'x',
      trigger: media,
      dragClickables: true,
      inertia: flick,
      edgeResistance: 0.9,
      bounds: { minX: 0, maxX: width },
      // Press anywhere on the image → jump the divider to the pointer, then
      // track the drag from there (feels direct, not relative-to-current-spot).
      // For grab-the-handle-only instead, set `trigger: '[data-tcl-ba-handle]'`.
      //
      // MOUSE ONLY, AND THAT IS NOT A DETAIL. Draggable fires onPress straight from
      // its pointerdown/touchstart handler with no movement threshold — `minimumMovement`
      // gates when a DRAG starts, not when a press registers. So on touch, the first
      // frame of a vertical scroll gesture is a press, and the divider snaps to
      // wherever the thumb landed.
      //
      // With one slider per page (the homepage, below the fold) that was invisible and
      // the press-to-jump feel was a deliberate desktop affordance. Phase 6 D's results
      // page is a VERTICAL STACK of full-width sliders — 2 today, up to 48 at
      // max_treatments 12 x max_pairs 4 — and that stack is the page's main scroll path.
      // Every slider a thumb crossed on the way down would have its divider rearranged,
      // silently: vertical scrolling still works (Draggable writes touch-action: pan-y
      // over the whole trigger subtree), so nothing feels broken while it happens.
      //
      // A mouse never scrolls by dragging, so gating on pointer type keeps the desktop
      // affordance exactly as it was and costs touch users only the jump — drag still
      // works, it just starts from the current divider position, which is the
      // conventional behaviour for a comparison slider on touch anyway.
      onPress: function () {
        var ev = this.pointerEvent;
        if (ev && (ev.pointerType === 'touch' || String(ev.type).indexOf('touch') === 0)) return;
        var x = clamp(this.pointerX - media.getBoundingClientRect().left, 0, width);
        gsap.set(proxy, { x: x });
        this.update(true); // resync Draggable's x to the moved proxy (applyBounds)
        setPosition((x / width) * 100);
      },
      onDrag: fromProxy,
      onThrowUpdate: fromProxy,
    })[0];

    function fromProxy() {
      setPosition((dragInstance.x / width) * 100);
    }

    // Move the proxy programmatically (init / keyboard / range) and mirror it.
    function goTo(pct) {
      pct = setPosition(pct);
      gsap.set(proxy, { x: pctToX(pct) });
      dragInstance.update();
    }

    // Keyboard + assistive tech drive the native range → move the proxy to match.
    range.addEventListener('input', function () {
      goTo(parseFloat(range.value));
    });

    // Keep bounds and proxy position correct across resizes / rotations.
    function refresh() {
      width = media.clientWidth || 1;
      dragInstance.applyBounds({ minX: 0, maxX: width });
      gsap.set(proxy, { x: pctToX(parseFloat(range.value)) });
      dragInstance.update();
    }
    if ('ResizeObserver' in window) {
      new ResizeObserver(refresh).observe(media);
    } else {
      window.addEventListener('resize', refresh);
    }

    // Prime from the Start position rendered server-side.
    goTo(parseFloat(range.value));
    root.setAttribute('data-tcl-ba-ready', '');
  }

  function init(node) {
    (node || document).querySelectorAll('[data-tcl-ba]').forEach(initSlider);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });

  // Theme editor: re-init when the section is added or reloaded.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  }
})();
